/**
 * Seafoam Liquid Glass Design System
 * HomeCEO Color Tokens - Supporting overwhelmed but optimistic moms
 * 
 * Design Philosophy:
 * - Seafoam mist creates calm and serenity
 * - Slate greys provide professional grounding
 * - Liquid glass effects add modern sophistication
 * - Cool-tinted neutrals maintain freshness
 * 
 * @version 2.0.0
 * @lastUpdated 2025-10-27
 */

// ============================================
// CODING AGENT GUIDE
// ============================================
/**
 * QUICK REFERENCE FOR AI AGENTS:
 * 
 * PRIMARY USES:
 * - Hero/Headers: brand.primary (#D8F3ED) with glass effects
 * - Buttons/CTAs: highlight.primary (#475569) with hover darkening
 * - Body Text: neutral.darkMedium (#475569)
 * - Cards: glass.light effect with white/seafoam tints
 * - Backgrounds: gradients.liquidGlass.seafoam for depth
 * 
 * GLASS EFFECTS:
 * - Always pair with backdrop-filter: blur()
 * - Use rgba values for proper transparency
 * - Add subtle borders for definition
 * 
 * ACCESSIBILITY:
 * - Minimum contrast 4.5:1 for normal text
 * - Use highlight.dark for headings on light backgrounds
 * - Ensure glass overlays maintain readability
 */

// ============================================
// Type Definitions
// ============================================

interface GradientStop {
  color: string;
  position: string;
}

interface GradientConfig {
  angle?: number;
  type?: 'linear' | 'radial' | 'conic';
  stops: ReadonlyArray<GradientStop>;
}

  // ============================================
  // Color Tokens
  // ============================================
  
  export const colors = {
    // Brand Colors - Seafoam Mist Family
    brand: {
      exxxxtraDark: '#1E4A42',
      exxxtraDark: '#2A675C',    // Deep seafoam for strong accents
      exxtraDark: '#3d9494',    // Deep seafoam for strong accents
      extraDark: '#99D5CA',    // Deep seafoam for strong accents
      primaryDark: '#B8E6DB',  // Rich seafoam for emphasis
      primary: '#D8F3ED',      // Main seafoam mist
      primaryLight: '#EBF9F6', // Soft seafoam for backgrounds
      extraLight: '#F5FCFB',   // Ultra-light seafoam tint
    },
  
    // Highlight Colors - Professional Slate Family
    highlight: {
      extraDark: '#1E293B',    // Deep slate for maximum contrast
      dark: '#334155',         // Dark slate for headings
      primary: '#475569',      // Main slate for CTAs
      light: '#64748B',        // Light slate for secondary elements
      extraLight: '#94A3B8',   // Soft slate for borders
      exxtraLight: '#b4becd',
    },
  
    // Neutral Colors - Cool-tinted Greys
    neutral: {
      extraDark: '#1E293B',    // Darkest grey for text
      dark: '#334155',         // Dark grey for headings
      darkMedium: '#475569',   // Medium-dark for body text
      medium: '#64748B',       // Medium grey for secondary text
      mediumLight: '#94A3B8',  // Light-medium for muted text
      light: '#CBD5E1',        // Light grey for borders
      extraLight: '#E2E8F0',   // Extra light for backgrounds
      exxtraLight: '#E2E8F0',
      ultraLight: '#F1F5F9',   // Ultra light for subtle tints
    },
  
    // Accent Colors with Liquid Glass Variations
    accent: {
      pink: {
        exxtraDark: '#BC397A',
        extraDark: '#EC4899',
        dark: '#F9A8D4',
        base: '#FBCFE8',
        light: '#FCE7F3',
        extraLight: '#FDF2F8',
      },
      blue: {
        exxtraDark: '#01699F',
        extraDark: '#0284C7',  // Rich ocean blue
        dark: '#38BDF8',       // Vibrant sky blue
        base: '#7DD3FC',       // Bright cyan blue
        light: '#BAE6FD',      // Soft sky blue
        extraLight: '#E0F2FE', // Ultra light blue
      },
      green: {
        exxtraDark: '#0C9467',
        extraDark: '#10B981',
        dark: '#86EFAC',
        base: '#BBF7D0',
        light: '#D9F99D',
        extraLight: '#ECFCCB',
      },
      yellow: {
        exxtraDark: '#C47E08',
        extraDark: '#F59E0B',
        dark: '#FCD34D',
        base: '#FDE68A',
        light: '#FEF3C7',
        extraLight: '#FFFBEB',
      },
      purple: {
        exxtraDark: '#554877',
        extraDark: '#6B5B95',  // Muted cool purple
        dark: '#9B90C2',       // Soft lavender purple
        base: '#C4B5FD',       // Light periwinkle
        light: '#DDD6FE',      // Very light lavender
        extraLight: '#F3F0FF', // Ultra light purple
      },
      coral: {
        exxxtraDark: '#C65A5A',
        exxtraDark: '#DF6565',
        extraDark: '#F87171',
        dark: '#FCA5A5',
        base: '#FBBCBC',
        light: '#FECACA',
        extraLight: '#FEE2E2',
        exxtraLight: '#FEEDED',
      },
      red: {
        exxtraDark: '#941616',
        extraDark: '#B91C1C',
        dark: '#EF4444',
        base: '#F87171',
        light: '#FCA5A5',
        extraLight: '#FEE2E2',
      },
      magenta: {
        exxtraDark: '#81168C',
        extraDark: '#A21CAF',
        dark: '#D946EF',
        base: '#E879F9',
        light: '#F0ABFC',
        extraLight: '#FAE8FF',
      },
      teal: {
        exxxtraDark: '#0C6E63',
        exxtraDark: '#109384',
        extraDark: '#14B8A6',
        dark: '#5EEAD4',
        base: '#99F6E4',
        light: '#CCFBF1',
        extraLight: '#F0FDFA',
      },
      orange: {
        exxtraDark: '#BB4609',
        extraDark: '#EA580C',
        dark: '#FB923C',
        base: '#FDBA74',
        light: '#FED7AA',
        extraLight: '#FFF7ED',
      },
      zen: {
        exxtraDark: '#0E3747',
        extraDark: '#124559',
        dark: '#19747E',
        base: '#1F9AA7',
        light: '#75D7DD',
        extraLight: '#D5F5F7',
      },
    },
  
    // Water - the app's calm-water field, mirrored from home-ceo-mobile
    // `tokens.colors.wee` and `surface.page`. Light on water is white at
    // partial alpha, so the seafoam underneath keeps its hue.
    wee: {
      page: '#D2F0EA',                            // measured mean of the lit water; shows before the canvas draws
      caustic: 'rgba(255, 255, 255, 0.3)',        // swell band body
      causticRidge: 'rgba(255, 255, 255, 0.42)',  // swell band ridge, the line the eye tracks
      glint: '#FFFFFF',                           // four-point star
      glintSoft: 'rgba(255, 255, 255, 0.55)',     // bokeh disc centre
      glintClear: 'rgba(255, 255, 255, 0)',       // bokeh disc edge

      // The story — the app's home Wish → Explore → Enact graphic.
      // The lantern: the house as pure light over a pool of depth, with a
      // halo that is a RIM, because a white glow directly behind a white
      // house swallows its subject.
      lanternCore: '#FFFFFF',
      lanternEdge: '#EBF9F6',
      lanternHaloInner: 'rgba(255, 255, 255, 0.12)',
      lanternHalo: 'rgba(255, 255, 255, 0.62)',
      lanternHaloMid: 'rgba(245, 252, 251, 0.22)',
      lanternHaloClear: 'rgba(245, 252, 251, 0)',
      depthPool: 'rgba(61, 148, 132, 0.52)',
      depthPoolClear: 'rgba(61, 148, 132, 0)',
      // Soft colour over the water as the options arrive — the brand
      // guide's "aurora" (sky, seafoam, rose, lavender), kept faint.
      washSky: 'rgba(125, 211, 252, 0.3)',
      washSea: 'rgba(216, 243, 237, 0.34)',
      washRose: 'rgba(251, 207, 232, 0.32)',
      washLilac: 'rgba(196, 181, 253, 0.32)',
      // The sunrise behind the house as it comes alive — the one burst.
      sunriseCore: 'rgba(255, 251, 235, 0.98)',
      sunriseGold: 'rgba(253, 230, 138, 0.92)',
      sunrisePeach: 'rgba(252, 165, 165, 0.8)',
      sunriseRose: 'rgba(249, 168, 212, 0.6)',
      sunriseLilac: 'rgba(196, 181, 253, 0.4)',
      sunriseClear: 'rgba(196, 181, 253, 0)',
      // The flash the instant it comes alive.
      burstCore: 'rgba(255, 251, 235, 0.95)',
      burstGold: 'rgba(253, 230, 138, 0.55)',
      burstClear: 'rgba(253, 230, 138, 0)',
      // The white wash that lightens the field back toward the surface.
      sunlitFaint: 'rgba(255, 255, 255, 0.14)',
      sunlit: 'rgba(255, 255, 255, 0.4)',
      // "A pass of shimmer across the mark is the house flourish."
      markShimmer: 'rgba(255, 255, 255, 0.72)',
      markShimmerClear: 'rgba(255, 255, 255, 0)',
      // The Explore card's resting house in the redesign mock (June,
      // 2026-09-18), sampled from its ink: periwinkle to lavender.
      exploreHouse: '#82A2D3',
      exploreHouseFoot: '#A995C3',
    },

    // A member's chat message, mirrored from home-ceo-mobile
    // `colors.cta.glassTop/glassBottom` and `border.glassWhite`: the coral
    // pair carried down to partial alpha — glass, not a coral slab — with
    // the alpha floor set so ink text on it holds 4.5:1.
    chat: {
      userGlassTop: 'rgba(252, 165, 165, 0.72)',
      userGlassBottom: 'rgba(248, 113, 113, 0.58)',
      glassRim: 'rgba(255, 255, 255, 0.65)',
    },

    // Semantic Colors (gentle, mom-friendly)
    semantic: {
      success: '#86EFAC',       // accent.green.dark - gentle success
      warning: '#FCD34D',       // accent.yellow.dark - soft warning
      error: '#EF4444',         // accent.red.dark - clear error (not too harsh)
      info: '#7DD3FC',         // accent.blue.base - soft info
      successBg: '#ECFCCB',    // accent.green.extraLight
      warningBg: '#FEF3C7',    // accent.yellow.light
      errorBg: '#FEE2E2',      // accent.red.extraLight
      infoBg: '#E0F2FE',       // accent.blue.extraLight
    },
  
    // Legacy Compatibility & Quick Access
    text: '#1E293B',           // neutral.extraDark
    textSecondary: '#475569',  // neutral.darkMedium
    textMuted: '#64748B',      // neutral.medium
    textLight: '#94A3B8',      // neutral.mediumLight
    
    background: '#FFFFFF',     // Pure white
    surface: '#F5FCFB',       // brand.extraLight
    surfaceHover: '#EBF9F6',  // brand.primaryLight
    
    primary: '#D8F3ED',        // brand.primary
    primaryDark: '#B8E6DB',    // brand.primaryDark
    primaryLight: '#EBF9F6',   // brand.primaryLight
    
    // Borders with seafoam tint
    border: 'rgba(184, 230, 219, 0.2)',      // Soft seafoam border
    borderLight: 'rgba(184, 230, 219, 0.1)', // Very light border
    borderDark: '#B8E6DB',                   // Solid seafoam border
    
    // Liquid Glass Overlays
    overlayWhite10: 'rgba(255, 255, 255, 0.1)',
    overlayWhite20: 'rgba(255, 255, 255, 0.2)',
    overlayWhite30: 'rgba(255, 255, 255, 0.3)',
    overlayWhite35: 'rgba(255, 255, 255, 0.35)',
    overlayWhite40: 'rgba(255, 255, 255, 0.4)',
    overlayWhite45: 'rgba(255, 255, 255, 0.45)',
    overlayWhite50: 'rgba(255, 255, 255, 0.5)',
    overlayWhite55: 'rgba(255, 255, 255, 0.55)',
    overlayWhite60: 'rgba(255, 255, 255, 0.6)',
    overlayWhite65: 'rgba(255, 255, 255, 0.65)',
    overlayWhite70: 'rgba(255, 255, 255, 0.7)',
    overlayWhite80: 'rgba(255, 255, 255, 0.8)',
    overlayWhite90: 'rgba(255, 255, 255, 0.9)',
    
    // Dark overlays for modals
    darkOverlay10: 'rgba(30, 41, 59, 0.1)',
    darkOverlay20: 'rgba(30, 41, 59, 0.2)',
    darkOverlay30: 'rgba(30, 41, 59, 0.3)',
    darkOverlay35: 'rgba(30, 41, 59, 0.35)',
    darkOverlay40: 'rgba(30, 41, 59, 0.4)',
    darkOverlay45: 'rgba(30, 41, 59, 0.45)',
    darkOverlay50: 'rgba(30, 41, 59, 0.5)',
    darkOverlay60: 'rgba(30, 41, 59, 0.6)',
    
    // Seafoam overlays for brand elements
    seafoamOverlay10: 'rgba(216, 243, 237, 0.1)',
    seafoamOverlay20: 'rgba(216, 243, 237, 0.2)',
    seafoamOverlay30: 'rgba(216, 243, 237, 0.3)',
    seafoamOverlay40: 'rgba(216, 243, 237, 0.4)',
    seafoamOverlay50: 'rgba(216, 243, 237, 0.5)',
    
    // Social Colors
    google: '#4285F4',
    facebook: '#1877F2',
    linkedin: '#0A66C2',
    
    // Focus States
    focusRing: 'rgba(184, 230, 219, 0.5)',
    focusRingDark: 'rgba(71, 85, 105, 0.5)',
    
    // White variants
    white: '#FFFFFF',
    softWhite: '#FEFEFE',
    offWhite: '#FAFBFC',

    // Semantic aliases for legacy usage
    success: '#86EFAC',
    warning: '#FCD34D',
    error: '#EF4444',
    info: '#7DD3FC',

    // Compatibility aliases for legacy tokens
    // Glass surfaces (mapped to seafoam glass aesthetic)
    glassSurface: 'rgba(216, 243, 237, 0.2)',
    glassSurfaceHover: 'rgba(255, 255, 255, 0.3)',
    glassBorder: 'rgba(184, 230, 219, 0.25)',

    // Legacy white overlay degrees → mapped to overlayWhite variants
    overlayDegrees10: 'rgba(255, 255, 255, 0.1)',
    overlayDegrees15: 'rgba(255, 255, 255, 0.15)',
    overlayDegrees20: 'rgba(255, 255, 255, 0.2)',
    overlayDegrees25: 'rgba(255, 255, 255, 0.25)',
    overlayDegrees30: 'rgba(255, 255, 255, 0.3)',
    overlayDegrees35: 'rgba(255, 255, 255, 0.35)',
    overlayDegrees40: 'rgba(255, 255, 255, 0.4)',

    // Legacy brand overlays → mapped to seafoam overlays
    brandOverlay10: 'rgba(216, 243, 237, 0.1)',
    brandOverlay15: 'rgba(216, 243, 237, 0.15)',
    brandOverlay20: 'rgba(216, 243, 237, 0.2)',

    // Legacy black overlays → mapped to dark slate overlays
    blackOverlay20: 'rgba(30, 41, 59, 0.2)',
    blackOverlay30: 'rgba(30, 41, 59, 0.3)',
    blackOverlay40: 'rgba(30, 41, 59, 0.4)',

    // Legacy focus ring alias
    brandFocus: 'rgba(184, 230, 219, 0.5)',

    // Legacy shadow aliases
    shadowSoft: 'rgba(30, 41, 59, 0.12)',
    shadowHard: 'rgba(30, 41, 59, 0.22)',

    // External Brand Colors
    external: {
      google: {
        blue: '#4285F4',
        green: '#34A853',
        yellow: '#FBBC05',
        red: '#EA4335',
      },
      microsoft: {
        primary: '#0078D4',
        secondary: '#00BCF2',
      },
      apple: {
        gray: '#A2AAAD',
      },
    },

    // Shadow values for consistent elevation
    shadows: {
      soft: '0 2px 8px rgba(0, 0, 0, 0.15)',
      medium: '0 4px 12px rgba(0, 0, 0, 0.15)',
      elevated: '0 8px 20px rgba(0, 0, 0, 0.2)',
      modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      dropdown: '0 8px 24px rgba(12, 38, 61, 0.15)',
      card: '0 4px 12px rgba(12, 38, 61, 0.1)',
      cardHover: '0 8px 24px rgba(12, 38, 61, 0.15)',
      glass: '0 24px 48px rgba(12, 38, 61, 0.18)',
      glassMedium: '0 16px 32px rgba(12, 38, 61, 0.12)',
      glassLight: '0 20px 44px rgba(12, 38, 61, 0.18)',
      coralGlow: '0 4px 12px rgba(248, 113, 113, 0.3)',
      coralGlowHover: '0 6px 16px rgba(248, 113, 113, 0.4)',
      tealGlow: '0 4px 12px rgba(52, 140, 135, 0.3)',
      tealGlowHover: '0 6px 16px rgba(52, 140, 135, 0.4)',
      insetHighlight: 'inset 0 1px 0 rgba(255, 255, 255, 0.25)',
      insetHighlightStrong: 'inset 0 1px 0 rgba(255, 255, 255, 0.35)',
    },
  } as const;
  
  // ============================================
  // Gradient Configurations
  // ============================================
  
  export const gradients = {
    // Core Brand Gradients
    brandDark: {
      angle: 90,
      stops: [
        { color: colors.brand.exxtraDark, position: '0%' },
        { color: colors.brand.extraDark, position: '50%' },
        { color: colors.brand.primaryDark, position: '100%' },
      ],
    },
    
    brand: {
      angle: 135,
      stops: [
        { color: colors.brand.primaryDark, position: '0%' },
        { color: colors.brand.primary, position: '50%' },
        { color: colors.brand.primaryLight, position: '100%' },
      ],
    },
  
    brandLight: {
      angle: 135,
      stops: [
        { color: colors.brand.primary, position: '0%' },
        { color: colors.brand.primaryLight, position: '50%' },
        { color: colors.brand.extraLight, position: '100%' },
      ],
    },
  
    // Highlight Gradients
    highlight: {
      angle: 90,
      stops: [
        { color: colors.highlight.dark, position: '0%' },
        { color: colors.highlight.primary, position: '50%' },
        { color: colors.highlight.light, position: '100%' },
      ],
    },
  
    // Compatibility alias for lighter highlight gradient
    highlightLight: {
      angle: 135,
      stops: [
        { color: colors.highlight.light, position: '0%' },
        { color: colors.highlight.extraLight, position: '50%' },
        { color: colors.highlight.exxtraLight, position: '100%' },
      ],
    },

    highlightDark: {
      angle: 135,
      stops: [
        { color: colors.highlight.extraDark, position: '0%' },
        { color: colors.highlight.dark, position: '80%' },
        { color: colors.highlight.primary, position: '100%' },
      ],
    },

    // Compatibility alias for very light highlight gradient
    highlightVeryLight: {
      angle: 135,
      stops: [
        { color: colors.highlight.light, position: '0%' },
        { color: colors.highlight.extraLight, position: '20%' },
        { color: colors.highlight.exxtraLight, position: '100%' },
      ],
    },
  
    // Liquid Glass Gradients - NEW!
    liquidGlass: {
      // Main seafoam glass effect
      seafoam: {
        angle: 135,
        stops: [
          { color: colors.brand.primary, position: '0%' },
          { color: colors.brand.primaryLight, position: '50%' },
          { color: 'rgba(255, 255, 255, 0.9)', position: '100%' },
        ],
      },
      
      // Shimmer effect for hover states
      shimmer: {
        angle: 90,
        stops: [
          { color: 'rgba(216, 243, 237, 0.1)', position: '0%' },
          { color: 'rgba(216, 243, 237, 0.3)', position: '50%' },
          { color: 'rgba(216, 243, 237, 0.1)', position: '100%' },
        ],
      },
      
      // Bubble effect for floating elements
      bubble: {
        type: 'radial',
        stops: [
          { color: 'rgba(216, 243, 237, 0.4)', position: '0%' },
          { color: 'rgba(216, 243, 237, 0.2)', position: '50%' },
          { color: 'transparent', position: '70%' },
        ],
      },
      
      // Wave effect for loading states
      wave: {
        angle: 180,
        stops: [
          { color: 'rgba(255, 255, 255, 0)', position: '0%' },
          { color: 'rgba(216, 243, 237, 0.1)', position: '20%' },
          { color: 'rgba(216, 243, 237, 0.2)', position: '50%' },
          { color: 'rgba(216, 243, 237, 0.1)', position: '80%' },
          { color: 'rgba(255, 255, 255, 0)', position: '100%' },
        ],
      },
      
      // Aurora effect for special features
      aurora: {
        angle: 45,
        stops: [
          { color: 'rgba(125, 211, 252, 0.3)', position: '0%' },   // Updated blue base
          { color: 'rgba(216, 243, 237, 0.4)', position: '33%' },   // Seafoam
          { color: 'rgba(251, 207, 232, 0.3)', position: '66%' },   // Pink
          { color: 'rgba(196, 181, 253, 0.3)', position: '100%' },  // Updated purple base
        ],
      },
      
      // Frost effect for cards
      frost: {
        angle: 135,
        stops: [
          { color: 'rgba(255, 255, 255, 0.1)', position: '0%' },
          { color: 'rgba(216, 243, 237, 0.05)', position: '50%' },
          { color: 'rgba(255, 255, 255, 0.2)', position: '100%' },
        ],
      },
      
      // Mist effect for backgrounds
      mist: {
        type: 'radial',
        stops: [
          { color: 'rgba(235, 249, 246, 0.8)', position: '0%' },
          { color: 'rgba(235, 249, 246, 0.4)', position: '50%' },
          { color: 'transparent', position: '100%' },
        ],
      },
    },

    // Compatibility alias for radial highlight gradient
    highlightRadial: {
      type: 'radial',
      stops: [
        { color: colors.highlight.light, position: '0%' },
        { color: colors.highlight.primary, position: '70%' },
        { color: colors.highlight.dark, position: '100%' },
      ],
    },
  
    // Neutral Gradients
    neutral: {
      angle: 180,
      stops: [
        { color: colors.neutral.extraLight, position: '0%' },
        { color: colors.neutral.ultraLight, position: '100%' },
      ],
    },
  
    neutralDark: {
      angle: 180,
      stops: [
        { color: colors.neutral.dark, position: '0%' },
        { color: colors.neutral.darkMedium, position: '100%' },
      ],
    },
  
    // Accent Gradients
    accentPink: {
      angle: 135,
      stops: [
        { color: colors.accent.pink.dark, position: '0%' },
        { color: colors.accent.pink.base, position: '50%' },
        { color: colors.accent.pink.light, position: '100%' },
      ],
    },
    // Compatibility alias for dark radial variant
    accentPinkDark: {
      angle: 135,
      stops: [
        { color: colors.accent.pink.extraDark, position: '0%' },
        { color: colors.accent.pink.exxtraDark, position: '15%' },
        { color: colors.accent.pink.extraDark, position: '75%' },
        { color: colors.accent.pink.dark, position: '100%' },
      ],
    },
  
    accentBlue: {
      angle: 135,
      stops: [
        { color: colors.accent.blue.dark, position: '0%' },
        { color: colors.accent.blue.base, position: '50%' },
        { color: colors.accent.blue.light, position: '100%' },
      ],
    },
    accentBlueFull: {
      angle: 90,
      stops: [
        { color: colors.accent.blue.extraDark, position: '0%' },
        { color: colors.accent.blue.dark, position: '25%' },
        { color: colors.accent.blue.base, position: '50%' },
        { color: colors.accent.blue.light, position: '75%' },
        { color: colors.accent.blue.extraLight, position: '100%' },
      ],
    },
    accentBlueDark: {
      angle: 135,
      stops: [
        { color: colors.accent.blue.extraDark, position: '0%' },
        { color: colors.accent.blue.exxtraDark, position: '15%' },
        { color: colors.accent.blue.extraDark, position: '75%' },
        { color: colors.accent.blue.dark, position: '100%' },
      ],
    },

    // Facebook dark blue gradient
    facebookDarkBlue: {
      angle: 135,
      stops: [
        { color: '#0A4A9E', position: '0%' },
        { color: '#0D5DC7', position: '50%' },
        { color: '#5e9ae1', position: '100%' },
      ],
    },

    accentGreen: {
      angle: 135,
      stops: [
        { color: colors.accent.green.dark, position: '0%' },
        { color: colors.accent.green.base, position: '50%' },
        { color: colors.accent.green.light, position: '100%' },
      ],
    },
    accentGreenFull: {
      angle: 90,
      stops: [
        { color: colors.accent.green.extraDark, position: '0%' },
        { color: colors.accent.green.dark, position: '25%' },
        { color: colors.accent.green.base, position: '50%' },
        { color: colors.accent.green.light, position: '75%' },
        { color: colors.accent.green.extraLight, position: '100%' },
      ],
    },
    accentGreenDark: {
      angle: 135,
      stops: [
        { color: colors.accent.green.extraDark, position: '0%' },
        { color: colors.accent.green.exxtraDark, position: '15%' },
        { color: colors.accent.green.extraDark, position: '75%' },
        { color: colors.accent.green.dark, position: '100%' },
      ],
    },
  
    accentPurple: {
      angle: 135,
      stops: [
        { color: colors.accent.purple.dark, position: '0%' },
        { color: colors.accent.purple.base, position: '50%' },
        { color: colors.accent.purple.light, position: '100%' },
      ],
    },
    accentPurpleDark: {
      angle: 135,
      stops: [
        { color: colors.accent.purple.extraDark, position: '0%' },
        { color: colors.accent.purple.exxtraDark, position: '15%' },
        { color: colors.accent.purple.extraDark, position: '75%' },
        { color: colors.accent.purple.dark, position: '100%' },
      ],
    },
  
    accentYellow: {
      angle: 135,
      stops: [
        { color: colors.accent.yellow.dark, position: '0%' },
        { color: colors.accent.yellow.base, position: '50%' },
        { color: colors.accent.yellow.light, position: '100%' },
      ],
    },
    accentYellowFull: {
      angle: 90,
      stops: [
        { color: colors.accent.yellow.extraDark, position: '0%' },
        { color: colors.accent.yellow.dark, position: '25%' },
        { color: colors.accent.yellow.base, position: '50%' },
        { color: colors.accent.yellow.light, position: '75%' },
        { color: colors.accent.yellow.extraLight, position: '100%' },
      ],
    },
    accentYellowDark: {
      angle: 135,
      stops: [
        { color: colors.accent.yellow.extraDark, position: '0%' },
        { color: colors.accent.yellow.exxtraDark, position: '15%' },
        { color: colors.accent.yellow.extraDark, position: '75%' },
        { color: colors.accent.yellow.dark, position: '100%' },
      ],
    },
  
    accentTeal: {
      angle: 135,
      stops: [
        { color: colors.accent.teal.dark, position: '0%' },
        { color: colors.accent.teal.base, position: '50%' },
        { color: colors.accent.teal.light, position: '100%' },
      ],
    },
    accentTealFull: {
      angle: 90,
      stops: [
        { color: colors.accent.teal.extraDark, position: '0%' },
        { color: colors.accent.teal.dark, position: '25%' },
        { color: colors.accent.teal.base, position: '50%' },
        { color: colors.accent.teal.light, position: '75%' },
        { color: colors.accent.teal.extraLight, position: '100%' },
      ],
    },
    accentTealDark: {
      angle: 135,
      stops: [
        { color: colors.accent.teal.extraDark, position: '0%' },
        { color: colors.accent.teal.exxtraDark, position: '15%' },
        { color: colors.accent.teal.extraDark, position: '75%' },
        { color: colors.accent.teal.dark, position: '100%' },
      ],
    },
  
    accentOrange: {
      angle: 135,
      stops: [
        { color: colors.accent.orange.dark, position: '0%' },
        { color: colors.accent.orange.base, position: '50%' },
        { color: colors.accent.orange.light, position: '100%' },
      ],
    },
    accentOrangeFull: {
      angle: 90,
      stops: [
        { color: colors.accent.orange.extraDark, position: '0%' },
        { color: colors.accent.orange.dark, position: '25%' },
        { color: colors.accent.orange.base, position: '50%' },
        { color: colors.accent.orange.light, position: '75%' },
        { color: colors.accent.orange.extraLight, position: '100%' },
      ],
    },
    accentOrangeDark: {
      angle: 135,
      stops: [
        { color: colors.accent.orange.extraDark, position: '0%' },
        { color: colors.accent.orange.exxtraDark, position: '15%' },
        { color: colors.accent.orange.extraDark, position: '75%' },
        { color: colors.accent.orange.dark, position: '100%' },
      ],
    },
  
    accentCoral: {
      angle: 135,
      stops: [
        { color: colors.accent.coral.dark, position: '0%' },
        { color: colors.accent.coral.base, position: '50%' },
        { color: colors.accent.coral.light, position: '100%' },
      ],
    },

    accentCoralDark: {
      angle: 135,
      stops: [
        { color: colors.accent.coral.extraDark, position: '0%' },
        { color: colors.accent.coral.exxtraDark, position: '15%' },
        { color: colors.accent.coral.extraDark, position: '75%' },
        { color: colors.accent.coral.dark, position: '100%' },
      ],
    },

    accentCoralLight: {
      angle: 135,
      stops: [
        { color: colors.accent.coral.base, position: '0%' },
        { color: colors.accent.coral.light, position: '50%' },
        { color: colors.accent.coral.extraLight, position: '100%' },
      ],
    },

    accentCoralFull: {
      angle: 135,
      stops: [
        { color: colors.accent.coral.extraDark, position: '0%' },
        { color: colors.accent.coral.dark, position: '25%' },
        { color: colors.accent.coral.base, position: '50%' },
        { color: colors.accent.coral.light, position: '75%' },
        { color: colors.accent.coral.extraLight, position: '100%' },
      ],
    },
  
    accentRed: {
      angle: 135,
      stops: [
        { color: colors.accent.red.dark, position: '0%' },
        { color: colors.accent.red.base, position: '50%' },
        { color: colors.accent.red.light, position: '100%' },
      ],
    },
    accentRedFull: {
      angle: 90,
      stops: [
        { color: colors.accent.red.extraDark, position: '0%' },
        { color: colors.accent.red.dark, position: '25%' },
        { color: colors.accent.red.base, position: '50%' },
        { color: colors.accent.red.light, position: '75%' },
        { color: colors.accent.red.extraLight, position: '100%' },
      ],
    },
    accentRedDark: {
      angle: 135,
      stops: [
        { color: colors.accent.red.extraDark, position: '0%' },
        { color: colors.accent.red.exxtraDark, position: '15%' },
        { color: colors.accent.red.extraDark, position: '75%' },
        { color: colors.accent.red.dark, position: '100%' },
      ],
    },
  
    accentMagenta: {
      angle: 135,
      stops: [
        { color: colors.accent.magenta.dark, position: '0%' },
        { color: colors.accent.magenta.base, position: '50%' },
        { color: colors.accent.magenta.light, position: '100%' },
      ],
    },
    accentPinkFull: {
      angle: 90,
      stops: [
        { color: colors.accent.pink.extraDark, position: '0%' },
        { color: colors.accent.pink.dark, position: '25%' },
        { color: colors.accent.pink.base, position: '50%' },
        { color: colors.accent.pink.light, position: '75%' },
        { color: colors.accent.pink.extraLight, position: '100%' },
      ],
    },
    accentMagentaDark: {
      angle: 135,
      stops: [
        { color: colors.accent.magenta.extraDark, position: '0%' },
        { color: colors.accent.magenta.exxtraDark, position: '15%' },
        { color: colors.accent.magenta.extraDark, position: '75%' },
        { color: colors.accent.magenta.dark, position: '100%' },
      ],
    },
  
    // Hero Gradients
    heroGradient: {
      angle: 180,
      stops: [
        { color: 'rgba(235, 249, 246, 0.05)', position: '0%' },
        { color: 'rgba(216, 243, 237, 0.15)', position: '30%' },
        { color: 'rgba(216, 243, 237, 0.35)', position: '60%' },
        { color: 'rgba(255, 255, 255, 0.95)', position: '100%' },
      ],
    },
  
    // Mesh Gradient for complex backgrounds
    meshGradient: {
      type: 'radial',
      stops: [
        { color: colors.brand.primaryLight, position: '0%' },
        { color: colors.accent.blue.extraLight, position: '25%' },
        { color: colors.accent.green.extraLight, position: '50%' },
        { color: colors.brand.extraLight, position: '75%' },
        { color: colors.white, position: '100%' },
      ],
    },
  
    // ============================================
    // Data Visualization Gradients
    // ============================================
    
    // Classic heatmap: green (good) to red (bad)
    heatmap: {
      angle: 90,
      stops: [
        { color: colors.accent.green.extraDark, position: '0%' },
        { color: colors.accent.green.dark, position: '14%' },
        { color: colors.accent.green.base, position: '28%' },
        { color: colors.accent.green.light, position: '42%' },
        { color: colors.accent.yellow.base, position: '56%' },
        { color: colors.accent.yellow.dark, position: '70%' },
        { color: colors.accent.orange.dark, position: '84%' },
        { color: colors.accent.red.base, position: '100%' },
      ],
    },
  
    // Cool gradient for calming metrics
    coolGradient: {
      angle: 90,
      stops: [
        { color: colors.accent.teal.extraLight, position: '0%' },
        { color: colors.accent.teal.light, position: '20%' },
        { color: colors.accent.teal.base, position: '40%' },
        { color: colors.accent.teal.dark, position: '60%' },
        { color: colors.accent.teal.extraDark, position: '80%' },
        { color: '#0F766E', position: '100%' }, // Extra deep teal
      ],
    },
  
    // Warm gradient for energy metrics
    warmGradient: {
      angle: 90,
      stops: [
        { color: colors.accent.yellow.light, position: '0%' },
        { color: colors.accent.yellow.base, position: '20%' },
        { color: colors.accent.yellow.dark, position: '40%' },
        { color: colors.accent.orange.dark, position: '60%' },
        { color: colors.accent.red.base, position: '80%' },
        { color: colors.accent.red.dark, position: '100%' },
      ],
    },
  
    // Individual family gradients for monochromatic visualizations
    dataSeafoam: {
      angle: 90,
      stops: [
        { color: colors.brand.extraDark, position: '0%' },
        { color: colors.brand.primaryDark, position: '25%' },
        { color: colors.brand.primary, position: '50%' },
        { color: colors.brand.primaryLight, position: '75%' },
        { color: colors.brand.extraLight, position: '100%' },
      ],
    },
  
    dataBlue: {
      angle: 90,
      stops: [
        { color: colors.accent.blue.extraDark, position: '0%' },
        { color: colors.accent.blue.dark, position: '25%' },
        { color: colors.accent.blue.base, position: '50%' },
        { color: colors.accent.blue.light, position: '75%' },
        { color: colors.accent.blue.extraLight, position: '100%' },
      ],
    },
  
    dataGreen: {
      angle: 90,
      stops: [
        { color: colors.accent.green.extraDark, position: '0%' },
        { color: colors.accent.green.dark, position: '25%' },
        { color: colors.accent.green.base, position: '50%' },
        { color: colors.accent.green.light, position: '75%' },
        { color: colors.accent.green.extraLight, position: '100%' },
      ],
    },
  
    dataYellow: {
      angle: 90,
      stops: [
        { color: colors.accent.yellow.extraDark, position: '0%' },
        { color: colors.accent.yellow.dark, position: '25%' },
        { color: colors.accent.yellow.base, position: '50%' },
        { color: colors.accent.yellow.light, position: '75%' },
        { color: colors.accent.yellow.extraLight, position: '100%' },
      ],
    },
  
    dataOrange: {
      angle: 90,
      stops: [
        { color: colors.accent.orange.extraDark, position: '0%' },
        { color: colors.accent.orange.dark, position: '25%' },
        { color: colors.accent.orange.base, position: '50%' },
        { color: colors.accent.orange.light, position: '75%' },
        { color: colors.accent.orange.extraLight, position: '100%' },
      ],
    },
  
    dataRed: {
      angle: 90,
      stops: [
        { color: colors.accent.red.extraDark, position: '0%' },
        { color: colors.accent.red.dark, position: '25%' },
        { color: colors.accent.red.base, position: '50%' },
        { color: colors.accent.red.light, position: '75%' },
        { color: colors.accent.red.extraLight, position: '100%' },
      ],
    },
  
    dataPink: {
      angle: 90,
      stops: [
        { color: colors.accent.pink.extraDark, position: '0%' },
        { color: colors.accent.pink.dark, position: '25%' },
        { color: colors.accent.pink.base, position: '50%' },
        { color: colors.accent.pink.light, position: '75%' },
        { color: colors.accent.pink.extraLight, position: '100%' },
      ],
    },
  
    dataMagenta: {
      angle: 90,
      stops: [
        { color: colors.accent.magenta.extraDark, position: '0%' },
        { color: colors.accent.magenta.dark, position: '25%' },
        { color: colors.accent.magenta.base, position: '50%' },
        { color: colors.accent.magenta.light, position: '75%' },
        { color: colors.accent.magenta.extraLight, position: '100%' },
      ],
    },
  
    dataPurple: {
      angle: 90,
      stops: [
        { color: colors.accent.purple.extraDark, position: '0%' },
        { color: colors.accent.purple.dark, position: '25%' },
        { color: colors.accent.purple.base, position: '50%' },
        { color: colors.accent.purple.light, position: '75%' },
        { color: colors.accent.purple.extraLight, position: '100%' },
      ],
    },
  
    dataTeal: {
      angle: 90,
      stops: [
        { color: colors.accent.teal.extraDark, position: '0%' },
        { color: colors.accent.teal.dark, position: '25%' },
        { color: colors.accent.teal.base, position: '50%' },
        { color: colors.accent.teal.light, position: '75%' },
        { color: colors.accent.teal.extraLight, position: '100%' },
      ],
    },
  
    dataSlate: {
      angle: 90,
      stops: [
        { color: colors.neutral.extraDark, position: '0%' },
        { color: colors.neutral.dark, position: '25%' },
        { color: colors.neutral.darkMedium, position: '50%' },
        { color: colors.neutral.medium, position: '75%' },
        { color: colors.neutral.mediumLight, position: '100%' },
      ],
    },
  
    dataCoral: {
      angle: 90,
      stops: [
        { color: colors.accent.coral.extraDark, position: '0%' },
        { color: colors.accent.coral.dark, position: '25%' },
        { color: colors.accent.coral.base, position: '50%' },
        { color: colors.accent.coral.light, position: '75%' },
        { color: colors.accent.coral.extraLight, position: '100%' },
      ],
    },
  } as const;
  // ============================================
  // Shadows
  // ============================================
  
  export const shadows = {
    // Soft shadows for general use
    xs: '0 1px 2px rgba(30, 41, 59, 0.05)',
    sm: '0 2px 4px rgba(30, 41, 59, 0.06)',
    md: '0 4px 8px rgba(30, 41, 59, 0.08)',
    lg: '0 8px 16px rgba(30, 41, 59, 0.1)',
    xl: '0 12px 24px rgba(30, 41, 59, 0.12)',
    xxl: '0 24px 48px rgba(30, 41, 59, 0.15)',
    
    // Glass shadows
    glass: '0 8px 32px rgba(31, 38, 135, 0.1)',
    glassHover: '0 12px 48px rgba(31, 38, 135, 0.15)',
    
    // Colored shadows
    seafoam: '0 4px 24px rgba(184, 230, 219, 0.25)',
    slate: '0 4px 24px rgba(71, 85, 105, 0.15)',
    
    // Inset shadows for depth
    insetSm: 'inset 0 1px 2px rgba(30, 41, 59, 0.05)',
    insetMd: 'inset 0 2px 4px rgba(30, 41, 59, 0.08)',
    insetLg: 'inset 0 4px 8px rgba(30, 41, 59, 0.1)',
    
    // Glow effects
    glowSeafoam: '0 0 20px rgba(216, 243, 237, 0.5)',
    glowSlate: '0 0 20px rgba(71, 85, 105, 0.3)',
    glowWhite: '0 0 20px rgba(255, 255, 255, 0.5)',
  } as const;
  // ============================================
  // Utility Functions
  // ============================================
  
  /**
   * Convert a gradient config to CSS string
   */
  export function gradientToCSS(gradient: GradientConfig): string {
    const stops = gradient.stops
      .map((stop: GradientStop) => `${stop.color} ${stop.position}`)
      .join(', ');
  
    if (gradient.type === 'radial') {
      return `radial-gradient(circle, ${stops})`;
    } else if (gradient.type === 'conic') {
      return `conic-gradient(from ${gradient.angle || 0}deg, ${stops})`;
    }
  
    const angle = gradient.angle || 90;
    return `linear-gradient(${angle}deg, ${stops})`;
  }
